import React from 'react';

import {
  getHeaderBidder,
  IHeaderBidder,
  IHeaderBidderParamOption,
} from '~/src/dash/api/console-api';
import {
  getMagniteCTVTiersEnabledForPublisher,
  getMagniteCTVTiersForPublisher,
} from '~/src/dash/api/demand-settings/magnitectv/magnitectv-tiers_methods';
import { IMagniteCTVTier } from '~/src/dash/api/demand-settings/magnitectv/magnitectv-tiers_models';
import {
  getSamsungSSPTiersEnabledForPublisher,
  getSamsungSSPTiersForPublisher,
} from '~/src/dash/api/demand-settings/samsungssp/samsungssp-tiers_methods';
import { ISamsungSSPTier } from '~/src/dash/api/demand-settings/samsungssp/samsungssp-tiers_models';
import {
  getSpotxTiersEnabledForPublisher,
  getSpotxTiersForPublisher,
} from '~/src/dash/api/demand-settings/spotx/spotx-tiers_methods';
import { ISpotxTiers } from '~/src/dash/api/demand-settings/spotx/spotx-tiers_models';
import { EBool } from '~/src/dash/api/types/bool';
import { getSmartFloorConfigs, ISmartFloorConfigUI } from '~/src/dash/apiV2/smartFloors/crud';
import { ModelErrorPaper } from '~/src/dash/components/model-missing-permission';
import { IStore } from '~/src/dash/reducers/store';
import { parseLocation } from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { BidderSettingsFormView } from '~/src/dash/views/bidder-settings-form-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { ct } from '~/src/dash/publicaUI';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  headerbidder?: IHeaderBidder;
  headerbidderParamOptions?: Record<string, IHeaderBidderParamOption[]>;
  spotxTierEnabled: EBool;
  spotxTiers: ISpotxTiers[];
  magniteCTVTierEnabled: EBool;
  magniteCTVTiers: IMagniteCTVTier[];
  samsungSSPTierEnabled: EBool;
  samsungSSPTiers: ISamsungSSPTier[];
  smartFloorConfigs: ISmartFloorConfigUI[];
  err?: string;
}

export const BidderSettingsExistingView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      headerbidder: undefined,
      headerbidderParamOptions: undefined,
      spotxTierEnabled: EBool.EBool_FALSE,
      spotxTiers: [],
      magniteCTVTierEnabled: EBool.EBool_FALSE,
      magniteCTVTiers: [],
      samsungSSPTierEnabled: EBool.EBool_FALSE,
      samsungSSPTiers: [],
      smartFloorConfigs: [] as ISmartFloorConfigUI[],
      err: undefined,
    };

    // === TIERS ENABLED?
    getIsSpotxTiersEnabled() {
      getSpotxTiersEnabledForPublisher(this.props.session.publisher.ID).then(
        ([_isEnabled, err]) => {
          if (err) {
            console.error(err);
            this.setState({
              err: err.message,
            });
          } else if (_isEnabled !== undefined) {
            this.setState({
              spotxTierEnabled: _isEnabled,
            });
          }
        },
      );
    }

    getIsMagniteCTVTiersEnabled() {
      getMagniteCTVTiersEnabledForPublisher(this.props.session.publisher.ID).then(
        ([_isEnabled, err]) => {
          if (err) {
            console.error(err);
            this.setState({
              err: err.message,
            });
          } else if (_isEnabled !== undefined) {
            this.setState({
              magniteCTVTierEnabled: _isEnabled as EBool,
            });
          }
        },
      );
    }

    getIsSamsungSSPTiersEnabled() {
      getSamsungSSPTiersEnabledForPublisher(this.props.session.publisher.ID)
        .then((_isEnabled) => {
          if (_isEnabled !== undefined) {
            this.setState({
              samsungSSPTierEnabled: _isEnabled as EBool,
            });
          }
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            err: 'Unable to load tiers enable status',
          });
        });
    }

    // == TIERS
    getSpotxTiers() {
      getSpotxTiersForPublisher(this.props.session.publisher.ID).then(([_tiers, err]) => {
        if (err) {
          console.error(err);
          this.setState({
            err: err.message,
          });
        } else if (_tiers) {
          this.setState({
            spotxTiers: _tiers,
          });
        }
      });
    }

    getMagniteCTVTiers() {
      getMagniteCTVTiersForPublisher(this.props.session.publisher.ID).then(([_tiers, err]) => {
        if (err) {
          console.error(err);
          this.setState({
            err: err.message,
          });
        } else if (_tiers) {
          this.setState({
            magniteCTVTiers: _tiers as IMagniteCTVTier[],
          });
        }
      });
    }

    getSamsungSSPTiers() {
      getSamsungSSPTiersForPublisher(this.props.session.publisher.ID)
        .then((_tiers) => {
          if (_tiers) {
            this.setState({
              samsungSSPTiers: _tiers as ISamsungSSPTier[],
            });
          }
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            err: 'Unable to load tiers',
          });
        });
    }

    componentDidMount() {
      const loc = parseLocation(this.props.location.pathname);

      // get from API
      this.getIsSpotxTiersEnabled();
      this.getSpotxTiers();
      this.getIsMagniteCTVTiersEnabled();
      this.getMagniteCTVTiers();
      this.getIsSamsungSSPTiersEnabled();
      this.getSamsungSSPTiers();

      getHeaderBidder({
        query: {
          publisher_id: this.props.session.publisher.ID,
          bidder_id: loc.bidder,
        },
      })
        .then((headerbidder: IHeaderBidder) => {
          getSmartFloorConfigs(this.props.session.publisher.ID, headerbidder.ID, true)
            .then((smartFloorConfigs: ISmartFloorConfigUI[]) => {
              this.setState({
                headerbidder,
                smartFloorConfigs,
              });
            })
            .catch((err: string) => {
              this.setState({
                headerbidder,
                err,
              });
            });
        })
        .catch((err: string) => {
          console.error(err);
          this.setState({
            err,
          });
        });
    }

    render() {
      if (this.state.err !== undefined) {
        return <ModelErrorPaper err={this.state.err || ''} />;
      }
      return (
        <div>
          {this.state.headerbidder && this.props.user.bidderParameters ? (
            <BidderSettingsFormView
              headerbidder={this.state.headerbidder}
              headerbidderParamOptions={this.props.user.bidderParameters}
              existing
              bulkUpdateIDs={[]}
              isSpotxTierEnabled={this.state.spotxTierEnabled}
              spotxTiers={this.state.spotxTiers}
              isMagniteCTVTierEnabled={this.state.magniteCTVTierEnabled}
              magniteCTVTiers={this.state.magniteCTVTiers}
              isSamsungSSPTierEnabled={this.state.samsungSSPTierEnabled}
              samsungSSPTiers={this.state.samsungSSPTiers}
              smartFloorConfigs={this.state.smartFloorConfigs}
              cloneBidderEnabled={false}
            />
          ) : (
            <LoadingView active={true} text={ct('LoadingEllipsis')} />
          )}
        </div>
      );
    }
  },
);
